import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Download, RefreshCw } from "lucide-react";
import { useAdminRegister } from "@/hooks/use-admin-queries";
import { DatePicker } from "@/components/ui/date-picker";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AttendanceCellBadge,
  StatusLegend,
} from "@/components/attendance/attendance-cell";
import { OverrideDialog } from "@/components/admin/override-dialog";
import { ReportDownloadButtons } from "@/components/reports/report-download-buttons";
import { buildDailyReport } from "@/lib/reports/build-report";
import { todayIST, isBeforeMorningFinalize } from "@/lib/timezone";
import { adminApi } from "@/lib/api/admin";
import { getAdminPassword } from "@/lib/auth/storage";
import type {
  AttendanceCell,
  Category,
  OverrideStatus,
  RegisterFilter,
  RegisterColumn,
  SessionKey,
} from "@/lib/types";
import { CATEGORY_LABELS } from "@/lib/types";

const FILTERS: { value: RegisterFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "yoga", label: "YOGA" },
  { value: "pdc", label: "PDC" },
  { value: "bcaris", label: "Bcaris" },
  { value: "employees", label: "Employees" },
];

type OverrideTarget = {
  personId: string;
  personName: string;
  category: Category;
  sessionKey: SessionKey;
  sessionLabel: string;
  cell: AttendanceCell | null;
};

function columnsForGroup(
  allColumns: RegisterColumn[],
  category: string
): RegisterColumn[] {
  return allColumns.filter((c) => c.category === category);
}

export default function DailyRegisterPage() {
  const [date, setDate] = useState(todayIST());
  const [filter, setFilter] = useState<RegisterFilter>("all");
  const [overrideTarget, setOverrideTarget] = useState<OverrideTarget | null>(
    null
  );
  const queryClient = useQueryClient();
  const adminPassword = getAdminPassword();
  const { data, isLoading, error, refetch, isFetching } = useAdminRegister(
    date,
    filter
  );

  const recompute = useMutation({
    mutationFn: () => adminApi.recomputeDate(adminPassword!, date),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "register", date] });
    },
  });

  const saveOverride = useMutation({
    mutationFn: (payload: {
      status: OverrideStatus;
      late_minutes?: number;
      reason: string;
    }) =>
      adminApi.overrideSession(adminPassword!, {
        person_id: Number(overrideTarget!.personId),
        date,
        category: overrideTarget!.category,
        session: overrideTarget!.sessionKey,
        status: payload.status,
        late_minutes: payload.late_minutes,
        reason: payload.reason,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "register", date] });
      setOverrideTarget(null);
    },
  });

  const clearOverride = useMutation({
    mutationFn: () =>
      adminApi.clearSessionOverride(adminPassword!, {
        person_id: Number(overrideTarget!.personId),
        date,
        category: overrideTarget!.category,
        session: overrideTarget!.sessionKey,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "register", date] });
      setOverrideTarget(null);
    },
  });

  async function handleExport() {
    if (!adminPassword) return;
    const res = await adminApi.exportRegisterCsv(adminPassword, date, filter);
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `register-${date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const showPendingNote =
    date === todayIST() && isBeforeMorningFinalize() && !data?.morning_finalized;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Daily Register</h2>
          <p className="text-sm text-muted-foreground">
            Click a cell to manually override attendance
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DatePicker value={date} onChange={setDate} />
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw
              className={`mr-1 h-4 w-4 ${isFetching ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => recompute.mutate()}
            disabled={recompute.isPending}
          >
            Recompute
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="mr-1 h-4 w-4" />
            Export CSV
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/admin/shifts">Employee shifts</Link>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/admin/holidays">Holidays</Link>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/admin/sync">Sync devices</Link>
          </Button>
        </div>
      </div>

      {data?.holiday && (
        <p className="rounded-md border border-violet-200 bg-violet-50 px-3 py-2 text-sm text-violet-900">
          Holiday: <strong>{data.holiday.name}</strong> — cells show H where
          applicable (not counted absent).
        </p>
      )}

      {showPendingNote && (
        <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
          Before 7:00 AM IST, morning cells show pending (—). They become absent
          (A) after finalization — refresh or wait for auto-update.
        </p>
      )}

      <StatusLegend />

      <Tabs
        value={filter}
        onValueChange={(v) => setFilter(v as RegisterFilter)}
      >
        <TabsList>
          {FILTERS.map((f) => (
            <TabsTrigger key={f.value} value={f.value}>
              {f.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {isLoading && (
        <p className="text-muted-foreground">Loading register…</p>
      )}
      {error && (
        <p className="text-red-600">Could not load register for this date.</p>
      )}

      {data && (
        <div className="space-y-8">
          {data.groups.map((group) => {
            const cols = columnsForGroup(data.columns, group.category);
            if (group.rows.length === 0) return null;
            const groupReport = buildDailyReport(data, {
              groupCategory: group.category,
              title: `Daily Presence — ${CATEGORY_LABELS[group.category]}`,
            });
            const groupNote =
              group.category === "YOGA"
                ? "Morning program"
                : group.category === "PDC" || group.category === "BCARIS"
                  ? "Same morning schedule as Yoga"
                  : null;

            return (
              <section key={group.category} className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold">
                      {CATEGORY_LABELS[group.category]}
                    </h3>
                    {groupNote && (
                      <span className="text-sm text-muted-foreground">
                        ({groupNote})
                      </span>
                    )}
                    <Badge variant="secondary">{group.rows.length}</Badge>
                  </div>
                  <ReportDownloadButtons
                    report={groupReport}
                    filenameBase={`daily-presence-${group.category.toLowerCase()}-${date}`}
                  />
                </div>
                <div className="overflow-x-auto rounded-lg border bg-white">
                  <table className="w-full min-w-[480px] text-sm">
                    <thead>
                      <tr className="border-b bg-muted/40">
                        <th className="px-3 py-2 text-left font-medium">Name</th>
                        {cols.map((col) => (
                          <th
                            key={col.key}
                            className="px-3 py-2 text-center font-medium"
                          >
                            {col.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {group.rows.map((row) => (
                        <tr
                          key={`${row.person_id}-${group.category}`}
                          className="border-b last:border-0"
                        >
                          <td className="px-3 py-2 font-medium">
                            <div>{row.person_name}</div>
                            {row.shift_label && (
                              <div className="text-xs font-normal text-muted-foreground">
                                {row.shift_label}
                              </div>
                            )}
                          </td>
                          {cols.map((col) => {
                            const cell = row.columns[col.key as SessionKey] ?? null;
                            return (
                              <td key={col.key} className="px-3 py-2 text-center">
                                <button
                                  type="button"
                                  className="inline-flex rounded-md p-0.5 hover:bg-muted/60"
                                  title="Click to override"
                                  onClick={() =>
                                    setOverrideTarget({
                                      personId: row.person_id,
                                      personName: row.person_name,
                                      category: group.category,
                                      sessionKey: col.key,
                                      sessionLabel: col.label,
                                      cell,
                                    })
                                  }
                                >
                                  <AttendanceCellBadge
                                    cell={cell}
                                    showNotice
                                  />
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            );
          })}
          {data.groups.every((g) => g.rows.length === 0) && (
            <p className="text-muted-foreground">No records for this filter.</p>
          )}
        </div>
      )}

      <OverrideDialog
        open={overrideTarget !== null}
        onClose={() => setOverrideTarget(null)}
        personName={overrideTarget?.personName ?? ""}
        personId={overrideTarget?.personId ?? ""}
        date={date}
        category={overrideTarget?.category ?? "YOGA"}
        sessionKey={overrideTarget?.sessionKey ?? "morning"}
        sessionLabel={overrideTarget?.sessionLabel ?? ""}
        cell={overrideTarget?.cell ?? null}
        onSave={(payload) => saveOverride.mutate(payload)}
        onClear={() => clearOverride.mutate()}
        isSaving={saveOverride.isPending || clearOverride.isPending}
      />
    </div>
  );
}
